import { BalloonEventModel } from '../../models';
import type { GlobalBoundingBox } from '../../models/BalloonClusterModel';
import { apiFetch } from '../../utils/apiFetch';
import { pendingAutoFit } from '../../utils/pendingAutoFit';
import BeatAPIBalloonEventsRepository from './BeatAPIBalloonEventsRepository';

/**
 * v3-search variant of {@link BeatAPIBalloonEventsRepository}. Hits
 * `GET /api/v3/search` instead of `/api/search` and adds the `zoom` parameter,
 * but otherwise inherits the parent's full behavior — crucially including the
 * 5-quadrant cascade in {@link BalloonEventsRepository.loadQuery}:
 *
 *   1. First request fetches up to `amount` balloons for the full viewport.
 *   2. If the cap is hit, the parent computes which of the 5 sub-bboxes
 *      (CENTER, NW, NE, SW, SE) had no events in the top-N — these are the
 *      areas where balloons are "hiding" behind the cap.
 *   3. The parent then drills into each empty quadrant via `queryQuadrant`,
 *      which calls back into `_fetchBalloonEventsQuery(cancel, quadrant)` —
 *      the override below — fetching another `amount` balloons for that
 *      smaller bbox specifically.
 *   4. All hits accumulate in the inherited `_idToEvent` cache, so the
 *      frontend supercluster always sees the full union and cluster counts
 *      stay consistent across zoom changes.
 *
 * This is the previous developer's adaptive-tiling design — the v3 path was
 * accidentally bypassing it before this change. The clusters that v3 itself
 * may emit (when `ENABLE_CLUSTERING=true` on the backend) are dropped here:
 * the supercluster pipeline owns clustering on the frontend now.
 */
export default class BeatAPISearchV3Repository extends BeatAPIBalloonEventsRepository {
  private _zoom: number = 10;

  /** Map zoom level used by the next /v3/search request. */
  public set zoom(z: number) {
    this._zoom = z;
  }

  public get zoom(): number {
    return this._zoom;
  }

  private _globalBoundingBox: GlobalBoundingBox | null = null;

  /**
   * Viewport-independent bounding box of the whole filtered set. Populated only
   * when the last full-viewport request carried `include_global_bbox=1` (armed
   * via {@link pendingAutoFit}) AND the backend returned a box. null when the
   * flag wasn't set, no events matched, or the payload was malformed. The map
   * component reads this to fit/fly once, then disarms the signal.
   */
  public get globalBoundingBox(): GlobalBoundingBox | null {
    return this._globalBoundingBox;
  }

  /**
   * Override the v2 fetch to hit `/api/v3/search` and pass `zoom`. Mirrors the
   * parent's structure (per-quadrant abort controllers + `_getQueryObject`)
   * so the inherited `loadQuery` quadrant cascade works without further code.
   * Backend cluster items (if any) are discarded — supercluster handles
   * clustering on the frontend.
   */
  protected async _fetchBalloonEventsQuery(
    cancel: boolean,
    quadrant?: number
  ): Promise<BalloonEventModel[]> {
    // /api/v3/search requires topLeft + bottomRight (SearchV3Request). The
    // first effect-driven loadQuery on mount runs before the map has emitted
    // its first moveend, so query.bbox is still undefined — skip rather than
    // hit the network and surface a noisy 422 in the integrator's console.
    // The next loadQuery (fired from moveend) will carry the bbox.
    if (!this.query.bbox) {
      return [];
    }

    let abortController =
      quadrant !== undefined
        ? this._quadrantAbortControllers[quadrant]
        : this._abortController;
    if (abortController && cancel) {
      abortController.abort('params changed');
    }
    abortController = new AbortController();
    if (quadrant !== undefined) {
      this._quadrantAbortControllers[quadrant] = abortController;
    } else {
      this._abortController = abortController;
    }

    const regionAreas = [
      ...(this.query.regionArea || []),
      ...(this.query.region || []),
    ];

    const queryObject = this._getQueryObject(quadrant);
    queryObject.zoom = this._zoom;

    // Auto-fit: only the full-viewport request (never the quadrant drill-downs)
    // opts into the global bbox and captures the response. Reset first so a
    // stale bbox from a previous search never leaks into this one. The signal
    // is consumed either by the map (after it fits a returned bbox) or below
    // (when an armed request comes back with no bbox to fit).
    if (quadrant === undefined) {
      this._globalBoundingBox = null;
      if (pendingAutoFit.isArmed()) {
        queryObject.include_global_bbox = '1';
      }
    }

    const params = new URLSearchParams();
    for (const key in queryObject) {
      let q = queryObject[key];
      if (Array.isArray(q)) {
        q = q.join(',');
      }
      params.append(key, q);
    }

    const { signal } = abortController;
    const response = await apiFetch(
      this._apiEndpoints.searchV3 + '?' + params.toString(),
      {
        signal,
        method: 'GET',
        cache: 'no-cache',
        apiKey: this._apiKey,
        partnerKey: this._partnerKey,
      }
    );

    try {
      const data = await response.json();
      const rawItems: any[] = Array.isArray(data?.data) ? data.data : [];

      const events = rawItems
        .map((hit: any) => {
          // /v3/search may emit cluster items when backend clustering is on;
          // the frontend supercluster path doesn't use them — drop here.
          if (hit?.type === 'cluster') return null;
          try {
            const transformed = this._transfomer.transform(hit);
            for (const area of regionAreas) {
              transformed.addRegionArea(area);
            }
            return transformed;
          } catch (e) {
            if (this._logs) console.error('[v3] transform failed', e, hit);
            return null;
          }
        })
        .filter((e: BalloonEventModel | null): e is BalloonEventModel => e !== null);

      // Capture the opt-in global bbox from the full-viewport response only.
      if (quadrant === undefined) {
        const bbox = data?.globalBoundingBox;
        const coords =
          bbox && Array.isArray(bbox.topLeft) && Array.isArray(bbox.bottomRight)
            ? [Number(bbox.topLeft[0]), Number(bbox.topLeft[1]), Number(bbox.bottomRight[0]), Number(bbox.bottomRight[1])]
            : null;
        if (coords && coords.every((n) => Number.isFinite(n))) {
          this._globalBoundingBox = {
            topLeft: [coords[0], coords[1]],
            bottomRight: [coords[2], coords[3]],
          };
        } else if (response.ok && pendingAutoFit.isArmed()) {
          // The armed full-viewport request was serviced (2xx) but produced no
          // usable bbox — e.g. the filtered set has zero events, so the backend
          // returned globalBoundingBox: null. There is nothing for the map to
          // fit, so consume the one-shot here; otherwise it would stay armed and
          // a later search that DOES return a bbox (e.g. after the filter is
          // cleared) would fit unexpectedly. This is the robust place to disarm
          // because EventData's reference-equality guard skips the effect on a
          // null→null transition, so a map-side disarm would not always run.
          // A failed request (e.g. 422 before the viewport is known) is left
          // armed so the next valid request can still fit.
          pendingAutoFit.disarm();
        }
      }

      if (this._logs) {
        console.log(
          '\n --- BeatAPISearchV3Repository ---\n',
          this._apiEndpoints.searchV3,
          { quadrant, queryObject },
          `${events.length} events`
        );
      }
      return events;
    } catch (e) {
      console.error('[v3] response parse failed', response, e);
      return [];
    }
  }
}
